import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { IoAdd, IoSearch, IoFilter, IoDownload, IoRefresh, IoEye, IoCreate, IoTrash } from "react-icons/io5";
import { getToken } from '../../../../utils/authHelper';
import "./CustomerManagementEnhanced.css";
import { showMessageBox } from '../../../site/fn';
import { EmptyState, ErrorState } from '../../../../shared-ui/States';

const fetchWithAuth = async (url) => {
  const token = getToken();

  if (!token) {
    console.error("Authentication token not found. API call cancelled.");
    return null;
  }

  const response = await fetch(url, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    throw new Error(`API call failed: ${response.statusText}`);
  }
  return response.json();
};

function CustomerManagementEnhanced() {
  const navigate = useNavigate();
  const [usersMap, setUsersMap] = useState({});
  const [vendorTypeMap, setVendorTypeMap] = useState({});
  const [stateMap, setStateMap] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    active: "",
    vendor_type: "",
    state_id: "",
  });
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [refreshKey, setRefreshKey] = useState(0);
  const [selectedCustomers, setSelectedCustomers] = useState([]);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const userApiUrl = `${process.env.REACT_APP_NODE_SERVICE_BASE_URL}/api/get-column/users/name`;
        const users = await fetchWithAuth(userApiUrl);

        if (!users) return;

        const userMapping = users.reduce((acc, user) => {
          acc[user.id] = user.value;
          return acc;
        }, {});

        setUsersMap(userMapping);
      } catch (error) {
        console.error("Failed to fetch user data:", error);
      }
    };

    const fetchVendorTypes = async () => {
      try {
        const vendorApiUrl = `${process.env.REACT_APP_NODE_SERVICE_BASE_URL}/api/get-column/common_master/vendor_type`;
        const vendorTypes = await fetchWithAuth(vendorApiUrl);

        if (!vendorTypes) return;

        const vendorMapping = vendorTypes.reduce((acc, type) => {
          acc[type.id] = type.value;
          return acc;
        }, {});

        setVendorTypeMap(vendorMapping);
      } catch (error) {
        console.error("Failed to fetch vendor types:", error);
      }
    };

    const fetchStates = async () => {
      try {
        const stateApiUrl = `${process.env.REACT_APP_NODE_SERVICE_BASE_URL}/api/get-column/state_master/state_name`;
        const states = await fetchWithAuth(stateApiUrl);

        if (!states) return;

        const stateMapping = states.reduce((acc, state) => {
          acc[state.id] = state.value;
          return acc;
        }, {});

        setStateMap(stateMapping);
      } catch (error) {
        console.error("Failed to fetch state data:", error);
      }
    };

    const fetchCustomers = async () => {
      try {
        setIsLoading(true);
        setLoadError(null);
        const listApi = `${process.env.REACT_APP_NODE_SERVICE_BASE_URL}/customer/api/customers`;
        const response = await fetchWithAuth(listApi);
        if (response) {
          const rows = Array.isArray(response) ? response : [];
          setCustomers(
            [...rows].sort((a, b) => Number(b.id) - Number(a.id))
          );
        }
      } catch (error) {
        console.error("Failed to fetch customers:", error);
        setLoadError(error.message || "Failed to fetch customers.");
      } finally {
        setIsLoading(false);
      }
    };

    fetchUsers();
    fetchVendorTypes();
    fetchStates();
    fetchCustomers();
  }, [refreshKey]);

  const filteredCustomers = useMemo(() => {
    return customers.filter((customer) => {
      // Search filter
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        const matchesSearch = 
          customer.name?.toLowerCase().includes(searchLower) ||
          customer.email_id?.toLowerCase().includes(searchLower) ||
          customer.contact_details?.toLowerCase().includes(searchLower) ||
          customer.gst_no?.toLowerCase().includes(searchLower);
        if (!matchesSearch) return false;
      }

      // Active filter
      if (filters.active !== "") {
        const isActive = filters.active === "true";
        if (customer.active !== isActive) return false;
      }

      // Vendor type filter
      if (filters.vendor_type !== "") {
        if (customer.vendor_type !== parseInt(filters.vendor_type)) return false;
      }

      // State filter
      if (filters.state_id !== "") {
        if (customer.state_id !== parseInt(filters.state_id)) return false;
      }

      return true;
    });
  }, [customers, searchTerm, filters]);

  const handleSearch = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const clearFilters = () => {
    setFilters({
      active: "",
      vendor_type: "",
      state_id: "",
    });
    setSearchTerm("");
    setShowFilters(false);
  };

  const applyFilters = () => {
    setShowFilters(false);
  };

  const handleExport = async () => {
    try {
      const exportUrl = `${process.env.REACT_APP_NODE_SERVICE_BASE_URL}/customer/api/customers/export?format=csv`;
      const response = await fetchWithAuth(exportUrl);
      
      if (response) {
        const blob = new Blob([response], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'customers.csv';
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      }
    } catch (error) {
      console.error("Failed to export customers:", error);
    }
  };

  const handleRefresh = () => {
    setRefreshKey(prev => prev + 1);
  };

  const handleCustomerAction = (action, customerId) => {
    switch (action) {
      case 'view':
        navigate(`/customers/${customerId}`);
        break;
      case 'edit':
        navigate(`/customers/${customerId}/edit`);
        break;
      case 'delete':
        if (window.confirm('Are you sure you want to delete this customer?')) {
          deleteCustomer(customerId);
        }
        break;
      default:
        break;
    }
  };

  const deleteCustomer = async (customerId) => {
    try {
      const token = getToken();
      const deleteUrl = `${process.env.REACT_APP_NODE_SERVICE_BASE_URL}/customer/api/customers/${customerId}`;
      
      const response = await fetch(deleteUrl, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        handleRefresh();
      } else {
        throw new Error('Failed to delete customer');
      }
    } catch (error) {
      console.error('Error deleting customer:', error);
      showMessageBox('Failed to delete customer', "error");
    }
  };

  const handleSelectCustomer = (customerId) => {
    setSelectedCustomers(prev => 
      prev.includes(customerId) 
        ? prev.filter(id => id !== customerId)
        : [...prev, customerId]
    );
  };

  const handleSelectAll = () => {
    if (selectedCustomers.length === filteredCustomers.length) {
      setSelectedCustomers([]);
    } else {
      setSelectedCustomers(filteredCustomers.map(c => c.id));
    }
  };

  const handleBulkAction = async (action) => {
    if (selectedCustomers.length === 0) {
      showMessageBox('Please select customers first', "error");
      return;
    }

    try {
      const token = getToken();
      
      switch (action) {
        case 'delete':
          if (window.confirm(`Are you sure you want to delete ${selectedCustomers.length} customers?`)) {
            await Promise.all(
              selectedCustomers.map(id => 
                fetch(`${process.env.REACT_APP_NODE_SERVICE_BASE_URL}/customer/api/customers/${id}`, {
                  method: 'DELETE',
                  headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                  }
                })
              )
            );
            handleRefresh();
            setSelectedCustomers([]);
          }
          break;
        case 'export':
          // Export selected customers
          const exportData = filteredCustomers.filter(c => selectedCustomers.includes(c.id));
          const csv = convertToCSV(exportData);
          const blob = new Blob([csv], { type: 'text/csv' });
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = 'selected-customers.csv';
          document.body.appendChild(a);
          a.click();
          window.URL.revokeObjectURL(url);
          document.body.removeChild(a);
          break;
        default:
          break;
      }
    } catch (error) {
      console.error('Bulk action failed:', error);
      showMessageBox('Bulk action failed', "error");
    }
  };

  const convertToCSV = (data) => {
    if (!data || data.length === 0) return '';
    
    const headers = Object.keys(data[0]);
    const csvHeaders = headers.join(',');
    
    const csvRows = data.map(row => {
      return headers.map(header => {
        const value = row[header];
        if (value === null || value === undefined) return '';
        if (typeof value === 'string' && value.includes(',')) return `"${value}"`;
        return value;
      }).join(',');
    });

    return [csvHeaders, ...csvRows].join('\n');
  };

  return (
    <div className="customer-management">
      <header className="customer-header">
        <div className="header-content">
          <h1>Customer Management</h1>
          <p>Manage customers and their details</p>
        </div>
        <div className="header-actions">
          <button className="btn btn-primary" onClick={() => navigate('/customers/new')}>
            <IoAdd /> New Customer
          </button>
        </div>
      </header>

      <div className="customer-controls">
        <div className="search-section">
          <div className="search-bar">
            <IoSearch className="search-icon" />
            <input
              type="text"
              placeholder="Search customers by name, email, GST, or contact..."
              value={searchTerm}
              onChange={handleSearch}
              className="search-input"
            />
          </div>
          <button className="btn btn-secondary" onClick={() => setShowFilters(!showFilters)}>
            <IoFilter /> Filters
          </button>
          <button className="btn btn-secondary" onClick={handleRefresh}>
            <IoRefresh /> Refresh
          </button>
          <button className="btn btn-success" onClick={handleExport}>
            <IoDownload /> Export
          </button>
        </div>

        {showFilters && (
          <div className="filters-panel">
            <div className="filter-row">
              <div className="filter-group">
                <label>Status</label>
                <select
                  value={filters.active}
                  onChange={(e) => handleFilterChange('active', e.target.value)}
                >
                  <option value="">All Status</option>
                  <option value="true">Active</option>
                  <option value="false">Inactive</option>
                </select>
              </div>
              <div className="filter-group">
                <label>Vendor Type</label>
                <select
                  value={filters.vendor_type}
                  onChange={(e) => handleFilterChange('vendor_type', e.target.value)}
                >
                  <option value="">All Types</option>
                  {Object.entries(vendorTypeMap).map(([id, name]) => (
                    <option key={id} value={id}>{name}</option>
                  ))}
                </select>
              </div>
              <div className="filter-group">
                <label>State</label>
                <select
                  value={filters.state_id}
                  onChange={(e) => handleFilterChange('state_id', e.target.value)}
                >
                  <option value="">All States</option>
                  {Object.entries(stateMap).map(([id, name]) => (
                    <option key={id} value={id}>{name}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="filter-actions">
              <button className="btn btn-primary" onClick={applyFilters}>
                Apply Filters
              </button>
              <button className="btn btn-secondary" onClick={clearFilters}>
                Clear
              </button>
            </div>
          </div>
        )}
      </div>

      {selectedCustomers.length > 0 && (
        <div className="bulk-actions">
          <span>{selectedCustomers.length} customers selected</span>
          <div className="bulk-buttons">
            <button className="btn btn-secondary" onClick={() => handleBulkAction('export')}>
              <IoDownload /> Export Selected
            </button>
            <button className="btn btn-danger" onClick={() => handleBulkAction('delete')}>
              <IoTrash /> Delete Selected
            </button>
          </div>
        </div>
      )}

      
      <div className="customer-table-wrapper">
        {isLoading ? (
          <div className="loading">Loading customers...</div>
        ) : loadError ? (
          <ErrorState
            title="Could not load customers"
            description={loadError}
            onRetry={() => setRefreshKey((k) => k + 1)}
          />
        ) : filteredCustomers.length === 0 ? (
          <EmptyState
            title="No customers"
            description={
              searchTerm || filters.active || filters.vendor_type || filters.state_id
                ? "No customers found matching your criteria."
                : "Customers will appear here once they are added."
            }
          />
        ) : (
          <table>
            <thead>
              <tr>
                <th>
                  <input
                    type="checkbox"
                    checked={selectedCustomers.length === filteredCustomers.length && filteredCustomers.length > 0}
                    onChange={handleSelectAll}
                  />
                </th>
                <th>Name</th>
                <th>Email</th>
                <th>Phone</th>
                <th>GST No</th>
                <th>State</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredCustomers.map((customer) => (
                <tr key={customer.id}>
                  <td>
                    <input
                      type="checkbox"
                      checked={selectedCustomers.includes(customer.id)}
                      onChange={() => handleSelectCustomer(customer.id)}
                    />
                  </td>
                  <td>{customer.name}</td>
                  <td>{customer.email_id || 'N/A'}</td>
                  <td>{customer.contact_details || 'N/A'}</td>
                  <td>{customer.gst_no || 'N/A'}</td>
                  <td>{stateMap[customer.state_id] || 'N/A'}</td>
                  <td>
                    <span className={`status-badge ${customer.active ? 'active' : 'inactive'}`}>
                      {customer.active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td>
                    <div className="actions-cell">
                      <button
                        className="view-button"
                        onClick={() => handleCustomerAction('view', customer.id)}
                        title="View"
                      >
                        <IoEye />
                      </button>
                      <button
                        className="edit-button"
                        onClick={() => handleCustomerAction('edit', customer.id)}
                        title="Edit"
                      >
                        <IoCreate />
                      </button>
                      <button
                        className="delete-button"
                        onClick={() => handleCustomerAction('delete', customer.id)}
                        title="Delete"
                      >
                        <IoTrash />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

export default CustomerManagementEnhanced;
